import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import ErrorDialog from '../components/ui/ErrorDialog';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { Eye, EyeOff, User, Mail, Lock, AlertCircle } from 'lucide-react';

function Profile() {
  const { user, reloadUser, passwordResetRequired } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isUpdatingRef = useRef(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_fa_enabled: false
  });

  const [errors, setErrors] = useState({});

  // Reload user data when component mounts to ensure fresh data
  useEffect(() => {
    if (reloadUser && !isUpdatingRef.current) {
      reloadUser().catch(() => {
        // Silently fail - user data might already be loaded
      });
    }
  }, [reloadUser]); // Only run on mount (reloadUser is memoized in AuthContext)

  useEffect(() => {
    if (user && !isUpdatingRef.current) {
      setFormData(prev => {
        // Only update two_fa_enabled if the user object has a different value
        // and it's not undefined/null (which would indicate it wasn't set)
        const shouldUpdateTwoFa = user.two_fa_enabled !== undefined && 
                                   user.two_fa_enabled !== prev.two_fa_enabled;
        
        return {
          ...prev,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          role: user.role || '',
          // Only update two_fa_enabled if it's explicitly different and defined
          // This prevents clearing the checkbox when user object is updated with stale data
          two_fa_enabled: shouldUpdateTwoFa ? user.two_fa_enabled : prev.two_fa_enabled
        };
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let sanitized = type === 'checkbox' ? checked : value;
    if (type !== 'checkbox' && typeof value === 'string') {
      if (name === 'first_name' || name === 'last_name') {
        sanitized = value.replace(/[^a-zA-Z0-9-]/g, '').replace(/^\d+/, '');
      } else if (name === 'role') {
        sanitized = value.replace(/[^a-zA-Z0-9 ]/g, '').replace(/^\d+/, '');
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : sanitized
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    // If password reset is required, password fields are mandatory
    if (passwordResetRequired) {
      if (!formData.current_password) {
        newErrors.current_password = 'Current password is required';
      }
      
      if (!formData.new_password) {
        newErrors.new_password = 'New password is required';
      } else if (formData.new_password.length < 4) {
        newErrors.new_password = 'Password must be at least 4 characters';
      }
      
      if (formData.new_password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    } else {
      // Only validate password fields if user wants to change password
      if (formData.new_password || formData.confirm_password || formData.current_password) {
        if (!formData.current_password) {
          newErrors.current_password = 'Current password is required to change password';
        }
        
        if (!formData.new_password) {
          newErrors.new_password = 'New password is required';
        } else if (formData.new_password.length < 4) {
          newErrors.new_password = 'Password must be at least 4 characters';
        }
        
        if (formData.new_password !== formData.confirm_password) {
          newErrors.confirm_password = 'Passwords do not match';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleToggle2FA = async () => {
    setLoading(true);
    try {
      const response = await authService.toggleTwoFA();
      const newTwoFaEnabled = response.two_fa_enabled;
      
      // Update formData with the new 2FA status
      setFormData(prev => ({
        ...prev,
        two_fa_enabled: newTwoFaEnabled
      }));
      
      // Reload user to sync state
      if (reloadUser) {
        await reloadUser();
      }
      
      toast.success(response.message || `2FA ${newTwoFaEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to toggle 2FA. Please try again.';
      setErrorMessage(errorMessage);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBasicInfo = async () => {
    // Validate basic fields
    if (!formData.first_name.trim()) {
      setErrors({ first_name: 'First name is required' });
      return;
    }
    if (!formData.last_name.trim()) {
      setErrors({ last_name: 'Last name is required' });
      return;
    }

    setLoading(true);
    
    try {
      // Update only first_name, last_name, and role (not email)
      const profileData = {
        first_name: formData.first_name,
        last_name: formData.last_name
      };
      // Only include role if it has a value
      if (formData.role && formData.role.trim()) {
        profileData.role = formData.role.trim();
      } else {
        profileData.role = null;
      }
      
      const updatedUser = await authService.updateProfile(profileData);
      
      toast.success('Profile updated successfully!');
      
      // Reload user in auth context to keep it in sync
      if (reloadUser) {
        isUpdatingRef.current = true; // Prevent useEffect from overwriting during reload
        try {
          await reloadUser();
        } finally {
          // Allow useEffect to run again after a short delay
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 100);
        }
      }
      
      // Update local form state with the response from the API
      if (updatedUser) {
        setFormData(prev => ({
          ...prev,
          first_name: updatedUser.first_name || '',
          last_name: updatedUser.last_name || '',
          email: updatedUser.email || '',
          // Preserve role from response, or keep current value if not in response
          role: updatedUser.role !== undefined ? (updatedUser.role || '') : prev.role,
          // Preserve two_fa_enabled
          two_fa_enabled: updatedUser.two_fa_enabled !== undefined ? (updatedUser.two_fa_enabled || false) : prev.two_fa_enabled
        }));
      } else {
        // Fallback: refresh user data if response doesn't have the data
        if (reloadUser) {
          const freshUser = await reloadUser();
          if (freshUser) {
            setFormData(prev => ({
              ...prev,
              first_name: freshUser.first_name || '',
              last_name: freshUser.last_name || '',
              email: freshUser.email || '',
              role: freshUser.role !== undefined ? (freshUser.role || '') : prev.role,
              two_fa_enabled: freshUser.two_fa_enabled !== undefined ? (freshUser.two_fa_enabled || false) : prev.two_fa_enabled
            }));
          }
        }
      }
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile. Please check the details and try again.';
      setErrorMessage(errorMessage);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      let profileUpdateSuccess = false;
      
      // Update profile information (skip if password reset is required and password change will happen)
      if (!passwordResetRequired || !formData.new_password) {
        const profileData = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          role: formData.role
        };
        
        try {
          await authService.updateProfile(profileData);
          profileUpdateSuccess = true;
        } catch (profileError) {
          // If password reset is required, allow password change even if profile update fails
          if (!passwordResetRequired) {
            throw profileError;
          }
        }
      }
      
      // Change password if provided
      if (formData.new_password && formData.current_password) {
        await authService.changePassword({
          current_password: formData.current_password,
          new_password: formData.new_password
        });
        
        // If password reset was required, it's now complete
        if (passwordResetRequired) {
          toast.success('Password updated successfully! You can now access all features.');
          // Reload user to get updated password_reset flag
          if (reloadUser) {
            await reloadUser();
          }
          // Allow navigation to dashboard
          navigate('/dashboard');
          return; // Exit early since password reset is complete
        }
      }
      
      if (profileUpdateSuccess && !passwordResetRequired) {
        toast.success('Profile updated successfully!');
      }
      
      // Refresh user data and update local form state to reflect saved values
      if (reloadUser) {
        isUpdatingRef.current = true; // Prevent useEffect from overwriting
        try {
          const freshUser = await reloadUser();
          if (freshUser) {
            // Update formData with fresh user data
            setFormData(prev => ({
              ...prev,
              first_name: freshUser.first_name || '',
              last_name: freshUser.last_name || '',
              email: freshUser.email || '',
              role: freshUser.role || '',
              two_fa_enabled: freshUser.two_fa_enabled !== undefined ? freshUser.two_fa_enabled : prev.two_fa_enabled,
              // Clear password fields after successful update
              current_password: '',
              new_password: '',
              confirm_password: ''
            }));
          } else {
            // If reloadUser fails, still clear password fields
            setFormData(prev => ({
              ...prev,
              current_password: '',
              new_password: '',
              confirm_password: ''
            }));
          }
        } finally {
          // Allow useEffect to run again after a short delay
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 100);
        }
      } else {
        // Clear password fields if reloadUser is not available
        setFormData(prev => ({
          ...prev,
          current_password: '',
          new_password: '',
          confirm_password: ''
        }));
      }
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile. Please check the details and try again.';
      setErrorMessage(errorMessage);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {passwordResetRequired && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Password Reset Required:</strong> You must update your password before you can access other features. Please change your password below.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="first_name">
                  <User className="h-4 w-4 inline mr-2" />
                  First Name
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className={errors.first_name ? 'border-red-500' : ''}
                />
                {errors.first_name && (
                  <p className="text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">
                  <User className="h-4 w-4 inline mr-2" />
                  Last Name
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className={errors.last_name ? 'border-red-500' : ''}
                />
                {errors.last_name && (
                  <p className="text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">
                <User className="h-4 w-4 inline mr-2" />
                Role
              </Label>
              <Input
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                placeholder="Enter your role"
                className={errors.role ? 'border-red-500' : ''}
              />
              {errors.role && (
                <p className="text-sm text-red-600">{errors.role}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="h-4 w-4 inline mr-2" />
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                readOnly
                placeholder="Enter your email"
                className={`${errors.email ? 'border-red-500' : ''} bg-gray-100 cursor-not-allowed`}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Update Button for Basic Info */}
            <div className="flex justify-start">
              <Button
                type="button"
                variant="outline"
                onClick={handleUpdateBasicInfo}
                disabled={loading}
                className="min-w-[120px]"
              >
                {loading ? 'Updating...' : 'Update'}
              </Button>
            </div>

            {/* Password Change Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
              {passwordResetRequired ? (
                <p className="text-sm text-red-600 mb-4 font-medium">
                  You must change your password to continue.
                </p>
              ) : (
                <p className="text-sm text-gray-600 mb-4">
                  Leave password fields blank if you don't want to change your password.
                </p>
              )}
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">
                    <Lock className="h-4 w-4 inline mr-2" />
                    Current Password {passwordResetRequired && <span className="text-red-600">*</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      id="current_password"
                      name="current_password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={formData.current_password}
                      onChange={handleInputChange}
                      placeholder="Enter current password"
                      className={errors.current_password ? 'border-red-500 pr-10' : 'pr-10'}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.current_password && (
                    <p className="text-sm text-red-600">{errors.current_password}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new_password">
                      <Lock className="h-4 w-4 inline mr-2" />
                      New Password {passwordResetRequired && <span className="text-red-600">*</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        id="new_password"
                        name="new_password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={formData.new_password}
                        onChange={handleInputChange}
                        placeholder="Enter new password"
                        className={errors.new_password ? 'border-red-500 pr-10' : 'pr-10'}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.new_password && (
                      <p className="text-sm text-red-600">{errors.new_password}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">
                      <Lock className="h-4 w-4 inline mr-2" />
                      Confirm New Password {passwordResetRequired && <span className="text-red-600">*</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirm_password"
                        name="confirm_password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirm_password}
                        onChange={handleInputChange}
                        placeholder="Confirm new password"
                        className={errors.confirm_password ? 'border-red-500 pr-10' : 'pr-10'}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.confirm_password && (
                      <p className="text-sm text-red-600">{errors.confirm_password}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button and 2FA Toggle */}
            <div className="flex justify-start gap-2">
              <Button
                type="submit"
                variant="outline"
                disabled={loading}
                className="min-w-[120px]"
              >
                {loading ? 'Updating...' : 'Update'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleToggle2FA}
                disabled={loading}
                className="min-w-[120px]"
              >
                {formData.two_fa_enabled ? 'Disable 2FA' : 'Enable 2FA'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
}

export default Profile;
